import { useEffect, useRef, useState } from "react";
import { Link2, Unlink, RefreshCw, CheckCircle2, AlertTriangle, Instagram, Facebook } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { tr } from "../lib/i18n";
import PosterSettingsFields from "./PosterSettingsFields";

const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="eyebrow">{label}</span>
    <div className="mt-2">{children}</div>
    {hint ? <p className="text-[11px] text-[var(--gray-400)] mt-1">{hint}</p> : null}
  </label>
);

const toForm = (d) => ({
  meta_app_id: d.meta_app_id, meta_config_id: d.meta_config_id, meta_graph_version: d.meta_graph_version, meta_app_secret: "",
  instagram_app_id: d.instagram_app_id, instagram_app_secret: "",
  instagram: d.instagram, facebook: d.facebook, whatsapp: d.whatsapp,
  default_hashtags: (d.default_hashtags || []).join(" "), brand_tone: d.brand_tone,
  poster_style: d.poster_style, auto_template: d.auto_template, auto_lang: d.auto_lang, auto_background: d.auto_background, auto_draft: d.auto_draft,
});

const statusTone = (s) => (s === "connected" ? "text-emerald-500" : s === "not_connected" ? "text-[var(--gray-400)]" : "text-amber-500");

function ConnectionCard({ kind, c, canEdit, onConnect, onDisconnect, busy }) {
  const connected = c.status !== "not_connected";
  const Icon = kind === "instagram" ? Instagram : Facebook;
  return (
    <div className="panel p-4" data-testid={`social-${kind}-card`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1 basis-64">
          <div className="font-semibold flex items-center gap-2"><Icon size={16} /> {tr(`x.social_card_${kind /* i18n-keys: instagram,facebook */}`)}</div>
          <div className={`text-sm font-semibold mt-1 ${statusTone(c.status)}`} data-testid={`social-${kind}-status`}>{tr(`x.social_status_${c.status /* i18n-keys: connected,expiring_soon,expired,not_connected */}`)}</div>
          {connected ? (
            <div className="text-sm mt-1 space-y-0.5">
              {kind === "facebook" && <div>{tr("x.facebook_page")}: <strong data-testid="social-page-name">{c.page_name}</strong></div>}
              <div>{tr("x.instagram")}: <strong data-testid={`social-${kind}-ig-handle`}><bdi>{c.ig_username ? `@${c.ig_username}` : "—"}</bdi></strong></div>
              <div className="text-xs text-[var(--gray-400)]">
                {tr("x.token_expires")}: <bdi data-testid={`social-${kind}-expiry`}>{c.token_expires_at ? c.token_expires_at.slice(0, 10) : "—"}</bdi>
                {c.connected_by ? ` · ${tr("x.connected_by")} ${c.connected_by}` : ""}
              </div>
            </div>
          ) : <p className="text-xs text-[var(--gray-400)] mt-1 max-w-xl">{tr(`x.social_help_${kind /* i18n-keys: instagram,facebook */}`)}</p>}
        </div>
        {canEdit && (
          <div className="flex flex-wrap gap-2">
            <button type="button" className="btn-primary" disabled={busy} onClick={onConnect} data-testid={`social-connect-${kind}-btn`}>
              {connected ? <RefreshCw size={14} /> : <Link2 size={14} />} {connected ? tr("x.reconnect") : tr(`x.connect_${kind /* i18n-keys: instagram,facebook */}`)}
            </button>
            {connected && (
              <button type="button" className="btn-secondary" disabled={busy} onClick={onDisconnect} data-testid={`social-disconnect-${kind}-btn`}>
                <Unlink size={14} /> {tr("x.disconnect")}
              </button>
            )}
          </div>
        )}
      </div>
      {kind === "facebook" && c.warning && (
        <div className="mt-3 text-sm p-3 rounded-lg bg-amber-500/10 text-amber-600 flex gap-2" data-testid="social-ig-warning">
          <AlertTriangle size={16} className="shrink-0 mt-0.5" /> {tr("x.social_personal_ig_warning")}
        </div>
      )}
    </div>
  );
}

function Routes({ r }) {
  const label = (v) => tr(v ? `x.route_${v}` : "x.route_none");
  return (
    <div className="panel p-4 text-sm space-y-1" data-testid="social-routes">
      <div className="eyebrow mb-1">{tr("x.posting_will_use")}</div>
      <div>{tr("x.instagram")}: <strong data-testid="social-route-instagram">{label(r?.instagram)}</strong></div>
      <div>{tr("x.facebook")}: <strong data-testid="social-route-facebook">{label(r?.facebook)}</strong></div>
      <p className="text-[11px] text-[var(--gray-400)] pt-1">{tr("x.social_no_passwords")}</p>
    </div>
  );
}

function SecretField({ label, name, form, set, cfg, canEdit }) {
  return (
    <Field label={label} hint={cfg[`${name}_set`] ? <>{tr("x.key_on_file_paste_to_replace", { p0: "" })} <bdi dir="ltr">{cfg[`${name}_hint`]}</bdi></> : tr("x.stored_encrypted")}>
      <input className="input" dir="ltr" type="password" autoComplete="off" value={form[name]} disabled={!canEdit}
        placeholder={cfg[`${name}_set`] ? cfg[`${name}_hint`] : tr("x.paste_key_here")} onChange={(e) => set(name, e.target.value)} data-testid={`social-${name.replace(/_/g, "-")}`} />
    </Field>
  );
}

function MetaAppFields({ form, set, cfg, canEdit }) {
  const text = (name, label, hint) => (
    <Field label={label} hint={hint}>
      <input className="input" dir="ltr" value={form[name] || ""} disabled={!canEdit} onChange={(e) => set(name, e.target.value)} data-testid={`social-${name.replace(/_/g, "-")}`} />
    </Field>
  );
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {text("meta_app_id", tr("x.meta_app_id"))}
      <SecretField label={tr("x.meta_app_secret")} name="meta_app_secret" form={form} set={set} cfg={cfg} canEdit={canEdit} />
      {text("instagram_app_id", tr("x.instagram_app_id_optional"), tr("x.instagram_app_id_hint"))}
      <SecretField label={tr("x.instagram_app_secret_optional")} name="instagram_app_secret" form={form} set={set} cfg={cfg} canEdit={canEdit} />
      {text("meta_config_id", tr("x.meta_login_config_id"), tr("x.meta_login_config_id_hint"))}
      <Field label={tr("x.redirect_address_for_meta")} hint={tr("x.redirect_address_hint")}>
        <input className="input" readOnly dir="ltr" value={`${window.location.origin}${cfg.callback_path}`} data-testid="social-redirect-uri" onFocus={(e) => e.target.select()} />
      </Field>
    </div>
  );
}

function BrandFields({ form, set, canEdit }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <Field label={tr("x.instagram_handle")}>
        <input className="input" dir="ltr" placeholder="@" value={form.instagram || ""} disabled={!canEdit} onChange={(e) => set("instagram", e.target.value)} data-testid="social-ig-display" />
      </Field>
      <Field label={tr("x.facebook_page_link")}>
        <input className="input" dir="ltr" placeholder="https://facebook.com/..." value={form.facebook || ""} disabled={!canEdit} onChange={(e) => set("facebook", e.target.value)} data-testid="social-fb-display" />
      </Field>
      <Field label={tr("x.whatsapp_business_number")}>
        <input className="input" dir="ltr" inputMode="tel" placeholder="+968 9123 4567" value={form.whatsapp || ""} disabled={!canEdit} onChange={(e) => set("whatsapp", e.target.value)} data-testid="social-wa-display" />
      </Field>
      <div className="md:col-span-2">
        <Field label={tr("x.default_hashtags")} hint={tr("x.default_hashtags_hint")}>
          <input className="input" dir="ltr" placeholder="#Muscat #Oman #UsedCars" value={form.default_hashtags || ""} disabled={!canEdit} onChange={(e) => set("default_hashtags", e.target.value)} data-testid="social-default-hashtags" />
        </Field>
      </div>
      <Field label={tr("x.brand_tone")}>
        <input className="input" maxLength={120} placeholder={tr("x.brand_tone_placeholder")} value={form.brand_tone || ""} disabled={!canEdit} onChange={(e) => set("brand_tone", e.target.value)} data-testid="social-brand-tone" />
      </Field>
    </div>
  );
}

export default function SocialAccountsPanel({ onSaved }) {
  const { can } = useAuth();
  const canEdit = can("social_accounts", "edit");
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [saveState, setSaveState] = useState("");
  const edits = useRef(0);
  const formRef = useRef(form);
  formRef.current = form;
  const set = (k, v) => { edits.current += 1; setSaveState(""); setForm((f) => ({ ...f, [k]: v })); };
  // A server answer only replaces the form if nothing was typed since that request started.
  const fill = (d, since = edits.current) => { setCfg(d); if (since === edits.current) setForm(toForm(d)); };
  const queue = useRef(Promise.resolve());

  const load = async () => {
    const since = edits.current;
    try { const { data } = await api.get("/social/config"); fill(data, since); } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  // Saves run one after another, each with the newest form, so an older save can never overwrite a newer choice.
  const save = (quiet) => {
    const job = queue.current.then(async () => {
      const since = edits.current;
      const body = { ...formRef.current };
      ["meta_app_secret", "instagram_app_secret"].forEach((k) => { if (!body[k]) delete body[k]; });
      setSaveState("saving");
      try {
        const { data } = await api.put("/social/config", body);
        fill(data, since);
        setSaveState(since === edits.current ? "saved" : "");
        onSaved?.(data);
        if (!quiet) toast.success(tr("x.saved"));
      } catch (e) { setSaveState("error"); throw e; }
    });
    queue.current = job.catch(() => {});
    return job;
  };
  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); } };
  const connect = (method) => run(async () => {
    await save(true);
    const { data } = await api.post("/social/meta/start", { origin: window.location.origin, method });
    window.location.assign(data.url);
  });
  const disconnect = (method) => {
    if (!window.confirm(tr("x.disconnect_social_confirm"))) return;
    run(async () => { await api.post("/social/meta/disconnect", { method }); await load(); toast.success(tr("x.disconnected")); });
  };
  const clearSecret = (name) => window.confirm(tr("x.remove_saved_key_confirm")) && run(async () => {
    const { data } = await api.put("/social/config", { [`clear_${name}`]: true }); fill(data);
  });

  if (!cfg) return <div className="panel p-5 text-sm">{tr("x.loading")}</div>;
  return (
    <div className="space-y-5" data-testid="social-accounts-panel">
      <p className="text-sm text-[var(--gray-400)] max-w-2xl">{tr("x.social_connect_help")}</p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ConnectionCard kind="instagram" c={cfg.instagram_connection} canEdit={canEdit} busy={busy} onConnect={() => connect("instagram")} onDisconnect={() => disconnect("instagram")} />
        <ConnectionCard kind="facebook" c={cfg.connection} canEdit={canEdit} busy={busy} onConnect={() => connect("facebook")} onDisconnect={() => disconnect("facebook")} />
      </div>
      <Routes r={cfg.routes} />
      <div className="panel p-4 space-y-3">
        <div className="eyebrow">{tr("x.meta_app")}</div>
        <MetaAppFields form={form} set={set} cfg={cfg} canEdit={canEdit} />
      </div>
      <div className="panel p-4 space-y-3">
        <div className="eyebrow">{tr("x.shown_on_website_and_posts")}</div>
        <BrandFields form={form} set={set} canEdit={canEdit} />
      </div>
      <div className="panel p-4 space-y-3">
        <div className="eyebrow">{tr("sg.style_title")}</div>
        <PosterSettingsFields form={form} set={set} canEdit={canEdit} />
      </div>
      {canEdit && (
        <div className="flex flex-wrap justify-end gap-2">
          {cfg.meta_app_secret_set && <button type="button" className="btn-secondary" disabled={busy} onClick={() => clearSecret("meta_app_secret")} data-testid="social-clear-secret">{tr("x.remove_app_secret")}</button>}
          {cfg.instagram_app_secret_set && <button type="button" className="btn-secondary" disabled={busy} onClick={() => clearSecret("instagram_app_secret")} data-testid="social-clear-ig-secret">{tr("x.remove_instagram_app_secret")}</button>}
          {saveState && (
            <span className={`self-center text-sm ${saveState === "error" ? "text-red-600" : "text-[var(--gray-400)]"}`} role="status" data-testid="social-save-state">
              {tr(`x.save_state_${saveState /* i18n-keys: saving,saved,error */}`)}
            </span>
          )}
          <button type="button" className="btn-primary" disabled={busy} onClick={() => run(() => save(false))} data-testid="social-save-btn">
            <CheckCircle2 size={14} /> {tr("x.save_changes")}
          </button>
        </div>
      )}
    </div>
  );
}
